/**
 * Trần chờ của MỌI cổng API dựng qua `createApiServices` — một knob cho cả app.
 *
 * Thứ tự ưu tiên, từ mạnh tới yếu:
 *
 *   1. `options.timeout` truyền tay lúc tạo cổng (ví dụ cổng nào đó biết mình đặc biệt chậm);
 *   2. `REACT_APP_API_TIMEOUT_MS` — biến chung, đặt một lần cho toàn app;
 *   3. `DEFAULT_API_TIMEOUT_MS` = 30 s, đúng hành vi đã có từ trước.
 *
 * Không đặt gì thì mọi thứ chạy y như cũ; đó là chủ đích, biến này chỉ để mở đường chứ không đổi
 * mặc định của ai.
 *
 * Vì sao cần: báo cáo M18 một tháng kéo 356.303 dòng action log (46,5 MB) từ `itemhistories`. Trên
 * prod service nằm cùng mạng với cụm nên dưới một giây, nhưng chạy BE ở máy mình và đọc cụm qua
 * VPN thì đo được **12–40 giây** — biến động của đường truyền, không phải của code (Mongo xử lý
 * 2,0 s, CPU Node 1,2 s). Trần cứng 30 s khiến trình duyệt tự huỷ giữa chừng và người dev đọc ra
 * "lỗi" cho một request vẫn đang chạy bình thường.
 *
 * ⚠️ Đây là knob của LÚC BUILD, không phải lúc chạy. CRA thay `process.env.REACT_APP_*` bằng giá
 *    trị literal ngay khi đóng gói, nên đổi nó trên một bản build đã deploy thì phải build lại. Ở
 *    máy dev: đặt trong `.env.local` (đã `.gitignore`) rồi khởi động lại `yarn start`.
 *
 * ⚠️ Phải viết NGUYÊN VĂN `process.env.REACT_APP_...`. CRA thay bằng phép thế chuỗi, nên
 *    `process.env[tenBien]` ra `undefined` và lặng lẽ rơi về mặc định.
 */

/** Hành vi đã có từ trước khi có biến môi trường nào — đừng đổi con số này để "cho nhanh". */
export const DEFAULT_API_TIMEOUT_MS = 30000

/**
 * Giá trị timeout dùng được, hoặc `fallback`.
 *
 * ⚠️ **`0` BỊ TỪ CHỐI, cố ý.** Với axios, `timeout: 0` nghĩa là *chờ mãi mãi* — một số 0 gõ nhầm
 *    vào `.env` sẽ treo màn hình tới tận TCP timeout của hệ điều hành mà không báo gì. Đây đúng
 *    cái bẫy `apiSku.js` đã ghi lại. `Infinity` cũng vậy và nguy hơn vì nó qua được phép so dấu:
 *    `Infinity > 0` là `true`, nên `Number.isFinite` mới là thứ chặn nó.
 *
 * ⚠️ Giá trị lạ (chữ, âm, rỗng) trả `fallback` chứ KHÔNG ném: một biến môi trường gõ sai không
 *    được phép làm chết cả app lúc khởi động.
 *
 * ⚠️ `fallback` BẮT BUỘC truyền, cố ý không có giá trị mặc định. Tham số mặc định của JS chỉ chạy
 *    khi đối số là `undefined`, nên `timeoutFromEnv(raw, undefined)` — đúng cách viết "không có
 *    lớp dưới" — sẽ lặng lẽ nhận lại chính giá trị mặc định đó. Bắt truyền tay thì không ai sập bẫy.
 */
export const timeoutFromEnv = (raw, fallback) => {
    const value = Number(raw)

    return Number.isFinite(value) && value > 0 ? Math.round(value) : fallback
}

/** Trần chung của app, sau khi đã đọc biến môi trường. */
export const API_TIMEOUT_MS = timeoutFromEnv(process.env.REACT_APP_API_TIMEOUT_MS, DEFAULT_API_TIMEOUT_MS)
